import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loader from '../../../loaders';
import { FiltersConverter, SortsConverter } from '../../../../helpers';
import api from "../../../../services/api";
import { Col, Row } from 'reactstrap';
import { TooltipButton } from '../../../inputs';
import { AgGridReact } from 'ag-grid-react';
import qs from "qs";
import { ErrorNotification } from '../../../alerts';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import { useMsal } from '@azure/msal-react';

const pageSize = 50;
const apiUrl = "termination";

export default function TerminationList() {
    const [loading, setLoading] = useState(false);
    const [popupMessageObj, setPopupMessageObj] = useState(null);
    const [isHR, setIsHR] = useState(false);
    const loadingOverlayComponent = useMemo(() => Loader, []);
    const { instance } = useMsal();
    const user = instance.getActiveAccount();
    const gridRef = useRef();

    useEffect(() => {
        api.get(`userprofile/group?username=${user.username}&group=HR`)
            .then(response => {
                // console.log({response});
                setIsHR(response?.data?.userProfileId ? true : false);
            })
            .catch(error => console.error({error}))
        ;
    }, [user]);

    const defaultColDef = useMemo(() => ({
        sortable: false,
        resizable: true,
        filter: true,
        editable: false,
        filter: 'agTextColumnFilter',
        filterParams: {
            buttons: ['reset'],
            debounceMs: 200,
            excelMode: 'mac',
        },
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true,
        },
        suppressMenu: true,
    }), []);

    const getServerSideDatasource = useCallback(() => {
        return {
            getRows: (params) => {
                const { request, requestId, action } = params;
                const { filterModel, sortModel } = request;

                const currentFilters = FiltersConverter(filterModel);
                const currentSorts = SortsConverter(sortModel);

                const queryString = qs.stringify({ filters: currentFilters, sortBy: currentSorts }, { allowDots: true });
                const url = `/${apiUrl}/${request.startRow ?? 0}/${pageSize}${requestId ? ("/" + requestId + "/" + action) : ""}${queryString ? `?${queryString}` : ""}`;

                api.get(url)
                    .then((response) => {
                        // console.log({response});
                        const successData = {
                            rowData: response.data.data,
                            rowCount: response.data.total,
                        };

                        params.success(successData);
                    })
                    .catch(error => {
                        params.fail();
                        console.error({error});
                    });
            },
        };
    }, [pageSize]);

    const onGridReady = useCallback((params) => {
        params.api.setServerSideDatasource(getServerSideDatasource());
        params.api.sizeColumnsToFit();
        window.addEventListener('resize', function () {
            setTimeout(function () {
                params.api.sizeColumnsToFit();
            });
        });
    }, [getServerSideDatasource]);

    const downloadDocument = useCallback((id, docType) => {
        setLoading(true);

        api.get(`${apiUrl}/attachment/${id}?docType=${docType}`, { responseType: 'blob' })
            .then(blob => {
                const filename = /(?<=attachment;.*filename=").*(?=";)/.exec(blob.headers["content-disposition"])[0]
                fileDownload(blob.data, filename);
            })
            .catch(error => {
                console.error(error)
            })
            .finally(() => setLoading(false))
        ;
    }, []);

    const columnDefs = [
        {
            field: "terminationNumber",
            headerName: "Exit Form Number",
            sort: "asc",
            minWidth: 100,
            sortable: true,
        },
        {
            field: "candidateName",
            headerName: "Employee",
            colId: "candidate.candidateName",
            minWidth: 150,
        },
        {
            field: "company.companyName",
            headerName: "Entity",
            minWidth: 180,
        },
        {
            field: "position.positionName",
            headerName: "Position",
            minWidth: 130,
        },
        {
            field: "project.projectName",
            headerName: "Project",
            minWidth: 150,
        },
        {
            field: "employmentType.lookupValue",
            headerName: "Employment Type",
            minWidth: 120,
        },
        {
            field: "manager.displayName",
            headerName: "Manager",
            minWidth: 150,
        },
        {
            field: "reason.lookupValue",
            headerName: "Reason",
            minWidth: 100,
        },
        {
            field: "status.lookupValue",
            headerName: "Status",
            minWidth: 100,
        },
        {
            field: "actions",
            headerName: "",
            filter: false,
            maxWidth: 90,
            minWidth: 90,
            pinned: "right",
            cellRenderer: (row) => {
                const {data: rowData} = row;
                const canEdit = rowData.status.lookupValue === "Initial";
                const {documentExists} = rowData;
                
                return (
                    <div key={`exit_action_${rowData.terminationId}`} id={`exit_action_${rowData.terminationId}`} className="text-center">
                        <TooltipButton
                            id={`exit_edit_${rowData.terminationId}`}
                            title={canEdit ? "Edit" : "View"}
                            className="btn-icon"
                            color="default"
                            size="xss"
                            type="button"
                            onClick={() => window.open(`/exitform/${rowData.terminationId}`, '_blank')}
                        >
                            <i className={`fa fa-xs ${canEdit ? "fa-pencil-alt" : "fa-search"} pt-1`}></i>
                        </TooltipButton>
                        <TooltipButton
                            id={`exit_generate_approval_${rowData.terminationId}`}
                            title={`Final Step${canEdit || isHR ? "" : " (View)"}`}
                            className="btn-icon"
                            color="default"
                            size="xss"
                            type="button"
                            onClick={() => window.open(`/exitformfinal/${rowData.terminationId}`, '_blank')}
                            disabled={rowData?.status?.lookupValue === "Initial" || rowData?.status?.lookupValue === "Pending Approval"}
                        >
                            <i className="fa fa-xs fa-step-forward pt-1"></i>
                        </TooltipButton>
                        {/* <TooltipButton
                            id={`exit_download_${rowData.terminationId}`}
                            title="Download Documents"
                            className="btn-icon"
                            color="default"
                            size="xss"
                            type="button"
                            onClick={() => downloadDocument(rowData.terminationId)}
                            disabled={!documentExists}
                        >
                            <i className="fa fa-xs fa-file-word pt-1"></i>
                        </TooltipButton> */}
                    </div>
                );
            }
        },
    ];

    return (
        <>
            {loading && <Loader />}
            <Row>
                <Col sm={12} className="d-flex flex-wrap justify-content-start align-items-center mb-2">
                    <TooltipButton
                        id="addnew-exit"
                        title="Add New Exit Form"
                        className="btn-icon"
                        color="default"
                        size="sm"
                        type="button"
                        onClick={() => window.open("/eccinitialform", '_blank')}
                    >
                        <i className="fas fa-plus pt-1"></i>
                    </TooltipButton>
                    <TooltipButton
                        id="export-excel-exit"
                        title="Export Exit Form to Excel"
                        className="btn-icon"
                        color="default"
                        size="sm"
                        type="button"
                        onClick={() => {}}
                    >
                        <i className="fas fa-file-excel pt-1"></i>
                    </TooltipButton>
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <div className="ag-theme-alpine ag-grid" style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
                        <AgGridReact
                            ref={gridRef}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            loadingOverlayComponent={loadingOverlayComponent}
                            suppressColumnVirtualisation={false}
                            suppressRowVirtualisation={false}
                            debounceVerticalScrollbar={false}
                            rowBuffer={20}
                            headerHeight={30}
                            groupHeaderHeight={30}
                            rowHeight={30}
                            floatingFiltersHeight={30}
                            suppressDragLeaveHidesColumns={true}
                            suppressColumnMoveAnimation={true}
                            animateRows={false}
                            pagination={true}
                            paginationPageSize={pageSize}
                            cacheBlockSize={pageSize}
                            rowModelType={'serverSide'}
                            serverSideInfiniteScroll={true}
                            onGridReady={onGridReady}
                            rowClass={'ag-row-normal'}
                        />
                    </div>
                </Col>
            </Row>
        {
            popupMessageObj?.type === "error" ?
            <ErrorNotification
                onConfirm={() => setPopupMessageObj(null)}
                message={popupMessageObj.message}
            />
            :
            null
        }
        </>
    );
};